'use client';

import useEmblaCarousel from 'embla-carousel-react';
import { useEffect, useMemo, useRef } from 'react';

import { Button, type ButtonProps } from '@/app/components/button';
import { ButtonGroup } from '@/app/components/button-group';
import { Divider } from '@/app/components/divider';
import { Container, ContainerStyled } from '@/app/components/layouts/container';
import { Section } from '@/app/components/layouts/section';
import { Image } from '@/app/components/media/image';
import { generateSources } from '@/app/components/media/image/generate-sources';
import { type ImageProps } from '@/app/components/media/image/types';
import { PaginationDash } from '@/app/components/navigation/pagination';
import { useInView } from '@/app/hooks/useInView';
import { useMediaSlider } from '@/app/hooks/useMediaSlider';
import { useRevealAnim } from '@/app/hooks/useRevealAnim';
import { cn } from '@/app/utils/cn';
import { createDataAttributes } from '@/app/utils/createDataAttributes';
import { createDataGA } from '@/app/utils/createDataGA';
import { handleSectionHrefClick } from '@/app/utils/scrollToSectionById';

const AUTOPLAY_DELAY_S = 10;

export interface SectionConceptProps {
  dataTest?: string;
  title: string;
  items: {
    image: Omit<ImageProps, 'aspectRatio'>;
    imageDesktop: Omit<ImageProps, 'aspectRatio'>;
    preTitle: string;
    title: string;
    description: string;
    buttons: ButtonProps[];
    remark?: string;
  }[];
}

export function SectionConcept({
  dataTest,
  title,
  items = [],
}: SectionConceptProps) {
  const ref = useRef<HTMLDivElement | null>(null);
  const isInView = useInView(ref, {
    rootMargin: '0px 0px -30% 0px',
    once: false,
  });

  useRevealAnim({
    groupOnce: true,
    itemOnce: true,
    groupMargin: '0px 0px -30% 0px',
    itemMargin: '0px 0px -30% 0px',
    defaultStagger: 0.1,
    defaultBaseDelay: 0.8,
  });
  const sliderItems = useMemo(() => items.map(() => undefined), [items]);

  const {
    paginationItemsRefs,
    currentIndex,
    currentPage,
    setEmblaApi,
    onPageChange,
  } = useMediaSlider({
    inView: isInView && items.length > 1,
    items: sliderItems,
    imageDuration: AUTOPLAY_DELAY_S,
  });

  const [emblaRef, emblaApi] = useEmblaCarousel({
    loop: items.length > 1,
    align: 'start',
    duration: 25,
  });

  useEffect(() => {
    if (!emblaApi) return;
    setEmblaApi(emblaApi);
  }, [emblaApi, setEmblaApi]);

  if (items.length === 0) return null;

  return (
    <Section
      ref={ref}
      className={cn('relative')}
      size="none"
      data-test={dataTest}
    >
      <h2 className="sr-only">{title}</h2>
      <div ref={emblaRef} className="overflow-hidden">
        <div className="flex will-change-transform">
          {items.map((item, index) => {
            const isActive = index === currentIndex && isInView;

            return (
              <div
                key={index}
                className={cn(
                  'max-lg:landscape:aspect-3-4 relative flex h-svh shrink-0 basis-full flex-col max-lg:landscape:h-auto'
                )}
                {...createDataAttributes({
                  test: dataTest ? `${dataTest}-item-${index}` : undefined,
                  active: index === currentIndex ? 'true' : null,
                })}
              >
                <div
                  className={cn(
                    'pointer-events-none absolute inset-0 z-0 overflow-hidden'
                  )}
                >
                  <Image
                    src={item.image.src}
                    alt={item.image.alt}
                    width={item.image.width}
                    height={item.image.height}
                    layout="fill"
                    objectFit="cover"
                    imageClassName="object-bottom lg:object-center"
                    className={cn(
                      'size-full origin-[70%_50%] scale-100 object-cover transition-transform delay-1000 duration-10000 ease-linear',
                      {
                        'scale-110 transform': isActive,
                      }
                    )}
                    sources={generateSources('overallLgDesktop', {
                      mobileSrc: item.image.src,
                      desktopSrc: item.imageDesktop.src,
                    })}
                  />
                </div>
                <Container
                  data-revealanim-group
                  className={cn(
                    'point relative z-10 flex flex-1 flex-col justify-between gap-4',
                    'xs:pb-19 pt-10 pb-17 sm:pt-14 sm:pb-23 lg:justify-center lg:gap-y-6'
                  )}
                >
                  <div className="text-white lg:max-w-124">
                    <h2
                      data-revealanim
                      data-order={0}
                      className="text-sub-h2 font-noto-bold where-span:text-inline-wrap"
                      dangerouslySetInnerHTML={{ __html: item.preTitle }}
                      {...createDataAttributes({ test: 'pre-title' })}
                    />
                    <h3
                      data-revealanim
                      data-order={1}
                      className="text-h2 font-display where-span:text-inline-wrap mt-2"
                      dangerouslySetInnerHTML={{ __html: item.title }}
                      {...createDataAttributes({ test: 'title' })}
                    />
                    <div data-revealanim data-order={2}>
                      <Divider />
                    </div>
                    <div
                      data-revealanim
                      data-order={3}
                      className="text-body-1 where-span:text-inline-wrap"
                      dangerouslySetInnerHTML={{ __html: item.description }}
                      {...createDataAttributes({ test: 'description' })}
                    />
                    {!!item.remark && (
                      <p
                        data-revealanim
                        data-order={4}
                        className="text-caption where-span:text-inline-wrap mt-1"
                        dangerouslySetInnerHTML={{ __html: item.remark }}
                      />
                    )}
                  </div>
                  <ButtonGroup
                    data-revealanim
                    data-order={item.remark ? 5 : 4}
                    className="w-full flex-wrap justify-center lg:justify-start"
                  >
                    {item.buttons.map((button, i) => (
                      <Button
                        key={i}
                        href={button.href}
                        color="white"
                        className="w-full md:w-auto"
                        onClick={(event) =>
                          handleSectionHrefClick(button.href, event)
                        }
                        {...createDataAttributes({
                          test: `cta-button-${index}`,
                        })}
                        {...createDataGA({
                          click: true,
                          eventName: 'concept-cta',
                          group: `concept-cta-${index}`,
                          url: button.href,
                          text: button.children as string,
                        })}
                      >
                        {button.children}
                      </Button>
                    ))}
                  </ButtonGroup>
                </Container>
              </div>
            );
          })}
        </div>
      </div>
      {items.length > 1 && (
        <div
          data-revealanim
          data-delay={1.2}
          className={cn(
            'lg:center-y absolute right-0 bottom-10 left-0 z-10 flex items-end justify-end lg:h-140',
            'pointer-events-none!'
          )}
        >
          <PaginationDash
            itemRefs={paginationItemsRefs}
            totalPages={items.length}
            currentPage={currentPage}
            onPageChange={onPageChange}
            isAnimated
            theme="light"
            wrapperClassName={cn(
              'pointer-events-auto justify-center lg:justify-start',
              ContainerStyled()
            )}
          />
        </div>
      )}
    </Section>
  );
}
